import React from "react";
import { resolveMediaUrl } from "./mediaUrl.js";

export function normalizeHeaderLogoLink(link) {
  return String(link ?? "").trim();
}

export function isExternalHeaderLogoLink(link) {
  return /^https?:\/\//i.test(normalizeHeaderLogoLink(link));
}

export function normalizeLinkTargetValue(linkTarget) {
  const value = String(linkTarget ?? "").trim();
  if (value === "_blank" || value === "_self") {
    return value;
  }
  return "";
}

export function resolveHeaderLinkTarget(link, linkTarget) {
  const normalizedTarget = normalizeLinkTargetValue(linkTarget);
  if (normalizedTarget) {
    return normalizedTarget;
  }
  return isExternalHeaderLogoLink(link) ? "_blank" : "_self";
}

export function readHeaderLogoTriggerNav(trigger) {
  if (!trigger) {
    return { link: "", target: "_self" };
  }

  const link =
    trigger.getAttribute("data-logo-link") ||
    trigger.dataset?.logoLink ||
    "";
  const target = normalizeLinkTargetValue(
    trigger.getAttribute("data-logo-target") || trigger.dataset?.logoTarget
  );

  return {
    link: normalizeHeaderLogoLink(link),
    target: target || resolveHeaderLinkTarget(link, ""),
  };
}

export function getHeaderLinkAttributes(link, linkTarget) {
  const resolvedTarget = resolveHeaderLinkTarget(link, linkTarget);
  if (resolvedTarget === "_blank") {
    return { target: "_blank", rel: "noopener noreferrer" };
  }
  return { target: "_self" };
}

function deriveLogoNameFromSrc(src) {
  if (!src || typeof src !== "string") {
    return "";
  }

  try {
    const pathname = src.includes("://") ? new URL(src).pathname : src;
    const filename = pathname.split("/").pop() || "";
    const base = filename.replace(/\.[^.]+$/, "").trim();
    if (!base || !isMeaningfulLogoFilename(base)) {
      return "";
    }

    return base
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  } catch {
    return "";
  }
}

function isMeaningfulLogoFilename(base) {
  const normalized = base.toLowerCase().trim();
  if (!normalized || normalized.length < 2) {
    return false;
  }

  if (/\d{8,}/.test(normalized)) {
    return false;
  }

  const digits = (normalized.match(/\d/g) || []).length;
  if (digits / normalized.length > 0.4) {
    return false;
  }

  const genericPrefix =
    /^(images?|img|upload|uploads|file|logo|asset|photo|picture|banner|icon)([-_\s]|$)/i;
  if (genericPrefix.test(normalized)) {
    const withoutPrefix = normalized.replace(genericPrefix, "").replace(/^[-_\s]+/, "");
    if (!withoutPrefix || /^[\d\-_\s]+$/.test(withoutPrefix)) {
      return false;
    }
  }

  return true;
}

export function resolveLogoAccessibleName({
  src,
  alt = "",
  accessibleName,
  logoIndex,
  confirmNavigation = false,
}) {
  const explicit = String(accessibleName ?? alt ?? "").trim();
  if (explicit) {
    return explicit;
  }

  const fromSrc = deriveLogoNameFromSrc(src);
  if (fromSrc) {
    return fromSrc;
  }

  if (Number.isInteger(logoIndex) && logoIndex > 0) {
    return `Logo ${logoIndex}`;
  }

  return confirmNavigation ? "Return to home page" : "Home";
}

export function buildLogoControlLabel(name, { confirmNavigation = false } = {}) {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) {
    return confirmNavigation ? "Return to home page" : "Home";
  }
  if (confirmNavigation) {
    return `${trimmed} – return to home page`;
  }
  return `${trimmed} home`;
}

export function isSamePageHeaderLink(link) {
  const normalized = normalizeHeaderLogoLink(link);
  if (!normalized || normalized === "#") {
    return true;
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    const target = new URL(normalized, window.location.href);
    return (
      target.origin === window.location.origin &&
      target.pathname === window.location.pathname &&
      target.search === window.location.search
    );
  } catch {
    return normalized === window.location.pathname;
  }
}

function renderLogoImage(src, altText) {
  return <img src={resolveMediaUrl(src)} alt={String(altText ?? "").trim()} />;
}

function renderLogoModalTrigger({
  className,
  ariaLabel,
  trimmedLink,
  linkTarget,
  image,
}) {
  const resolvedTarget = resolveHeaderLinkTarget(trimmedLink, linkTarget);

  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      data-bs-toggle="modal"
      data-bs-target="#logoPrompt"
      data-logo-link={trimmedLink}
      data-logo-target={resolvedTarget}
    >
      {image}
    </button>
  );
}

export function renderHeaderLogo({
  src,
  alt = "",
  accessibleName,
  logoIndex,
  link,
  linkTarget,
  className = "logo",
  fallbackSrc,
  useModalFallback = false,
  confirmNavigation = false,
}) {
  const imageSrc = src || fallbackSrc;
  if (!imageSrc) {
    return null;
  }

  const trimmedLink = normalizeHeaderLogoLink(link);
  const logoName = resolveLogoAccessibleName({
    src: imageSrc,
    alt,
    accessibleName,
    logoIndex,
    confirmNavigation: confirmNavigation || useModalFallback,
  });
  const controlLabel = buildLogoControlLabel(logoName, {
    confirmNavigation: confirmNavigation || useModalFallback,
  });
  const image = renderLogoImage(imageSrc, logoName);

  if (trimmedLink) {
    if (confirmNavigation || useModalFallback || isSamePageHeaderLink(trimmedLink)) {
      return renderLogoModalTrigger({
        className,
        ariaLabel: controlLabel,
        trimmedLink,
        linkTarget,
        image,
      });
    }

    return (
      <a
        href={trimmedLink}
        className={className}
        aria-label={controlLabel}
        {...getHeaderLinkAttributes(trimmedLink, linkTarget)}
      >
        {image}
      </a>
    );
  }

  if (useModalFallback) {
    return renderLogoModalTrigger({
      className,
      ariaLabel: controlLabel,
      trimmedLink: "",
      linkTarget: "_self",
      image,
    });
  }

  return <span className={className}>{image}</span>;
}
